// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { SettingsSectionMarker } from "..";
import { css } from "@emotion/react";
import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Virtuoso } from "react-virtuoso";

import {
  Button,
  DesignToken,
  Heading,
  SearchInput,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import { ExternalLink } from "components/external-link";
import { Icon, IconsEnum } from "components/icon";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { ActionLink } from "components/links/ActionLink";
import { HelpCenter } from "constants/help-center";
import { SettingsSection } from "constants/settings/sections";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { areShortcutValid, parseShortcuts } from "helpers/canned-responses-settings";
import { isRichTextMessageEmpty } from "helpers/text-editor";
import { withBreakpoints } from "helpers/withBreakpoints";
import { useDebounce } from "hooks/use-debounce";
import { useMacros } from "hooks/use-macros";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-named-as-default
import AttachmentProvider from "routes/tickets/ticket/message-box/richtext/AttachmentProvider";
import { selectAgentsAlphabetically } from "store/entities/agents";
import {
  ICannedResponse,
  getCanManageCannedResponses,
  getSettingsCannedResponses,
} from "store/entities/canned-responses";
import { mapCannedResponsesToUsage } from "store/entities/canned-responses/helpers";
import { selectRules } from "store/entities/rules/selectors";
import { selectCurrentAgentTeams } from "store/entities/teams";
import { SettingsViewActions, getSelectedTeamId } from "store/views/settings";
import { withVar } from "themes/helpers";
import { plural } from "utils/string";

import { CannedResponseContent } from "../../../_components/canned-response-content/CannedResponseContent";
import TeamSelection from "../TeamSelection";
import SettingsViewContainer from "../ViewContainer";
import { ContentWrapper, PageWrapper } from "../styled";

import CannedInformation from "./CannedInformation";
import CreateCannedResponse from "./CreateCannedResponse";
import { Shortcut } from "./Shortcut";
import UpdateCannedResponse from "./UpdateCannedResponse";
import { ShortcutWrapper, StyledShortcutsLabel } from "./styles";
import { FormValues } from "./types";

const CannedResponses: FC = () => {
  const dispatch = useDispatch();
  const [updateCannedResponseId, setUpdateCannedResponseId] = useState<string | null>(null);
  const [deleteCannedResponseId, setDeleteCannedResponseId] = useState<string | null>(null);
  const [createCannedResponseTransactionId, setCreateCannedResponseTransactionId] = useState<string | null>(null);
  const [updateCannedResponseTransactionId, setUpdateCannedResponseTransactionId] = useState<string | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [searchPhrase, setSearchPhrase] = useState("");
  const debouncedSearchPhrase = useDebounce<string>(searchPhrase);

  const agents = useSelector(selectAgentsAlphabetically);
  const teams = useSelector(selectCurrentAgentTeams);
  const cannedResponses = useSelector(getSettingsCannedResponses);
  const canManageCannedResponses = useSelector(getCanManageCannedResponses);
  const selectedTeamId = useSelector(getSelectedTeamId);

  const { macros } = useMacros();
  const rules = useSelector(selectRules);
  const cannedResponseUsage = useMemo(
    // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
    () => mapCannedResponsesToUsage(rules, macros, cannedResponses),
    [rules, macros, cannedResponses]
  );

  const filteredCannedResponses = useMemo(() => {
    if (!searchPhrase) {
      return cannedResponses;
    }

    return cannedResponses.filter(
      cannedResponse =>
        cannedResponse.text.toLowerCase().includes(debouncedSearchPhrase.toLowerCase()) ||
        cannedResponse.shortcuts.some(shortcut => shortcut.toLowerCase().includes(debouncedSearchPhrase.toLowerCase()))
    );
  }, [cannedResponses, searchPhrase, debouncedSearchPhrase]);

  const handleNormalMode = (): void => {
    setUpdateCannedResponseId(null);
    setDeleteCannedResponseId(null);
    setUpdateCannedResponseTransactionId(null);
    setCreateCannedResponseTransactionId(null);
  };

  const handleDeleteMode =
    (cannedResponseId: string): (() => void) =>
    () => {
      setDeleteCannedResponseId(cannedResponseId);
      setUpdateCannedResponseId(null);
    };

  const handleUpdateMode =
    (cannedResponse: ICannedResponse): (() => void) =>
    () => {
      const { id } = cannedResponse;
      setUpdateCannedResponseId(id);
      setDeleteCannedResponseId(null);
    };

  useEffect(() => {
    trackEngagement("Settings: Canned responses section opened", EventPlace.Settings);
  }, []);

  const handleTeamSelected = (teamId: string): void => {
    dispatch(SettingsViewActions.setTeamId({ teamId }));
  };

  const handleAddCannedResponse = (values: FormValues): void => {
    const { cannedRichText, cannedShortcuts } = values;

    if (
      (isRichTextMessageEmpty(cannedRichText) && !createCannedResponseTransactionId) ||
      !areShortcutValid(cannedShortcuts)
    ) {
      return;
    }

    dispatch(
      SettingsViewActions.addCannedResponse({
        // @ts-expect-error TS(2322)
        teamId: selectedTeamId,
        richTextObj: cannedRichText,
        shortcuts: parseShortcuts(cannedShortcuts),
        // @ts-expect-error TS(2322)
        transactionId: createCannedResponseTransactionId,
      })
    );

    setCreateCannedResponseTransactionId(null);
    setEditorKey(editorKey => editorKey + 1);
  };

  const handleTransactionChange = (transactionId: string): void => {
    const isInUpdateMode = updateCannedResponseId !== null;

    if (isInUpdateMode) {
      setUpdateCannedResponseTransactionId(transactionId);
    } else {
      setCreateCannedResponseTransactionId(transactionId);
    }
  };

  const handleUpdateCannedResponse =
    (cannedResponseId: string) =>
    (values: FormValues): void => {
      const { cannedRichText, cannedShortcuts } = values;

      dispatch(
        SettingsViewActions.updateCannedResponse({
          id: cannedResponseId,
          richTextObj: cannedRichText,
          shortcuts: parseShortcuts(cannedShortcuts),
          // @ts-expect-error TS(2322)
          transactionId: updateCannedResponseTransactionId,
        })
      );

      trackEngagement("Canned responses: Edited canned saved", EventPlace.Settings);

      handleNormalMode();
    };

  const handleDeleteCannedResponse = (): void => {
    // @ts-expect-error TS(2322)
    dispatch(SettingsViewActions.deleteCannedResponse({ id: deleteCannedResponseId }));

    handleNormalMode();
  };

  return (
    <SettingsViewContainer title="Canned responses">
      <SettingsSectionMarker section={SettingsSection.CannedResponses} />
      <PageWrapper>
        {/* @ts-expect-error TS(2322): Type 'unknown' is not assignable to type 'ITeam[]'... Remove this comment to see the full error message */}
        <TeamSelection teams={teams} selectedTeamId={selectedTeamId} includeAll onTeamSelected={handleTeamSelected} />
        <ContentWrapper data-test-id="canned-settings-wrapper">
          <Block marginBottom="37px">
            <Text>
              Use canned responses in the message area to solve customer cases quicker. Canned responses are pre-made
              messages that you can use while handling a ticket.{" "}
              <ExternalLink
                target="_blank"
                href={HelpCenter.WHAT_ARE_CANNED_RESPONSES}
                onClick={() => trackEngagement("Canned responses: Learn more clicked", EventPlace.Settings)}
              >
                Learn more
              </ExternalLink>
            </Text>
          </Block>
          {canManageCannedResponses && (
            <>
              {updateCannedResponseId ? (
                <CreateCannedResponse
                  editorKey={editorKey}
                  disabled={!!updateCannedResponseId}
                  handleAddCannedResponse={handleAddCannedResponse}
                  createCannedResponseTransactionId={createCannedResponseTransactionId}
                  teams={teams}
                  selectedTeamId={selectedTeamId}
                />
              ) : (
                <AttachmentProvider
                  key={editorKey}
                  transactionType="cannedResponse"
                  onTransactionChange={handleTransactionChange}
                >
                  <CreateCannedResponse
                    editorKey={editorKey}
                    handleAddCannedResponse={handleAddCannedResponse}
                    teams={teams}
                    selectedTeamId={selectedTeamId}
                    createCannedResponseTransactionId={createCannedResponseTransactionId}
                  />
                </AttachmentProvider>
              )}
            </>
          )}
          {cannedResponses.length > 0 && (
            <Flex column css={{ height: "100%" }}>
              <Block
                maxWidth="244px"
                width="100%"
                marginBottom={withVar(SpacingToken.Spacing2)}
                css={{ alignSelf: "flex-end" }}
              >
                <SearchInput value={searchPhrase} onChange={setSearchPhrase} placeholder="Search..." size="medium" />
              </Block>
              {filteredCannedResponses.length > 0 ? (
                <Virtuoso
                  style={{ height: "100%" }}
                  data={filteredCannedResponses}
                  itemContent={(index, cannedResponse) => {
                    const isCannedWithRichText = cannedResponse.richTextObj !== null;
                    const updateMode = updateCannedResponseId === cannedResponse.id;
                    const deleteMode = deleteCannedResponseId === cannedResponse.id;
                    const usageCount = cannedResponseUsage[cannedResponse.id]?.total || 0;
                    const isUsedInAutomation = usageCount > 0;

                    return (
                      <Flex
                        key={cannedResponse.id}
                        padding="16px 20px"
                        borderTop={`1px solid ${withVar(DesignToken.BorderBasicSecondary)}`}
                        css={css`
                          :hover {
                            background-color: ${withVar(DesignToken.SurfacePrimaryHover)};
                          }
                        `}
                      >
                        {updateMode && (
                          <AttachmentProvider
                            transactionType="cannedResponse"
                            onTransactionChange={handleTransactionChange}
                            cannedResponseID={cannedResponse.id}
                          >
                            <UpdateCannedResponse
                              cannedResponse={cannedResponse}
                              agents={agents}
                              handleNormalMode={handleNormalMode}
                              handleUpdateCannedResponse={handleUpdateCannedResponse}
                              isCannedWithRichText={isCannedWithRichText}
                            />
                          </AttachmentProvider>
                        )}
                        {!updateMode && (
                          <Flex gap={withVar(SpacingToken.Spacing6)} flexWrap width="100%">
                            <Flex
                              column
                              gap={withVar(SpacingToken.Spacing3)}
                              maxWidth="580px"
                              width="100%"
                              justifyContent="space-between"
                            >
                              <CannedResponseContent
                                cannedResponse={cannedResponse}
                                allowAttachmentDeletion
                                dataTestId={`canned-response-${index}`}
                              />
                              <div>
                                <StyledShortcutsLabel>shortcuts</StyledShortcutsLabel>
                                {cannedResponse.shortcuts.map((shortcut: string) => (
                                  <ShortcutWrapper data-testid={`canned-shortcut-${index}-${shortcut}`} key={shortcut}>
                                    <Shortcut value={shortcut} />
                                  </ShortcutWrapper>
                                ))}
                              </div>
                            </Flex>
                            <Flex column gap={withVar(SpacingToken.Spacing3)}>
                              {deleteMode ? (
                                <Block maxWidth="200px">
                                  {isUsedInAutomation ? (
                                    <Text>
                                      <Text bold>Delete canned response?</Text> You&apos;re about to delete a canned
                                      response that&apos;s included in {usageCount}{" "}
                                      {plural(usageCount, "automation", "automations")}. Are you sure you want to delete
                                      it?
                                    </Text>
                                  ) : (
                                    <Text>
                                      <Text bold>Delete canned response?</Text> You&apos;re about to delete a canned
                                      response, and it cannot be undone. Are you sure you want to delete it?
                                    </Text>
                                  )}
                                </Block>
                              ) : (
                                <CannedInformation cannedResponse={cannedResponse} agents={agents} />
                              )}
                              <Flex gap={withVar(SpacingToken.Spacing2)}>
                                {deleteMode ? (
                                  <>
                                    <Button size="compact" kind="destructive" onClick={handleDeleteCannedResponse}>
                                      Delete
                                    </Button>
                                    <Button size="compact" kind="secondary" onClick={handleNormalMode}>
                                      Cancel
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <ActionLink
                                      onClick={handleUpdateMode(cannedResponse)}
                                      data-testid={`edit-canned-response-${cannedResponse.shortcuts}`}
                                    >
                                      Edit
                                    </ActionLink>
                                    <ActionLink
                                      warning
                                      onClick={handleDeleteMode(cannedResponse.id)}
                                      data-testid={`delete-canned-response-${cannedResponse.shortcuts}`}
                                    >
                                      Delete
                                    </ActionLink>
                                  </>
                                )}
                              </Flex>
                            </Flex>
                          </Flex>
                        )}
                      </Flex>
                    );
                  }}
                />
              ) : (
                <Flex
                  padding={`${withVar(SpacingToken.Spacing12)} ${withVar(SpacingToken.Spacing6)}`}
                  justifyContent="center"
                  alignItems="center"
                  gap={withVar(SpacingToken.Spacing4)}
                  column
                >
                  <Icon name={IconsEnum.CannedDrawnGray} width="96px" height="96px" />
                  <Heading size="lg" as="h3">
                    No canned responses found
                  </Heading>
                  <Text size="md" as="span">
                    Adjust your search criteria or show all canned responses in this team.
                  </Text>
                  <Button kind="secondary" onClick={() => setSearchPhrase("")}>
                    Show all responses
                  </Button>
                </Flex>
              )}
            </Flex>
          )}
        </ContentWrapper>
      </PageWrapper>
    </SettingsViewContainer>
  );
};

export default withBreakpoints(CannedResponses);
